import { Box, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import ActionLink from 'components/action-link/action-link.component';
import { useCustomDialog } from 'components/custom-dialog/custom-dialog.hooks';
import CheckoutPromoCode from 'components/promo-code/checkout-promo-code/checkout-promo-code.component';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import TotalItem from '../../../components/total-item/total-item.component';
import {
  selectCharges,
  selectDisplayOrderSubTotal,
  selectDisplayTotalWithoutGratuityWithTip,
  selectOrderAndItemDiscounts,
  selectOrderType,
  selectTaxes,
} from '../../../redux/cart/cart.selectors';
import CheckoutSection from '../checkout-section/checkout-section.component';
import AppliedOffer from './applied-offer/applied-offer.component';
import CheckoutTip from './checkout-tip/checkout-tip.component';
import { useCheckoutTip } from './checkout-tip/checkout-tip.hooks';
import { getRoundedAmount } from 'utils';
import {
  makeSelectTipSubtitle,
  selectCheckoutTipHeader,
  selectCheckoutTipSubtitleEnabled,
} from 'redux/config/config.selectors';

const CheckoutSummarySection = ({ ...rest }) => {
  const { t } = useTranslation();
  const subtotal = useSelector(selectDisplayOrderSubTotal);
  const displayTotalWithTip = useSelector(
    selectDisplayTotalWithoutGratuityWithTip,
  );
  const charges = useSelector(selectCharges);
  const taxes = useSelector(selectTaxes);
  const { show: showTip, tip } = useCheckoutTip();
  const discounts = useSelector(selectOrderAndItemDiscounts);
  const checkoutTipHeader = useSelector(selectCheckoutTipHeader);
  const orderType = useSelector(selectOrderType);
  const checkoutTipSubtitleEnabled = useSelector(
    selectCheckoutTipSubtitleEnabled,
  );
  const selectCheckoutTipSubtitle = useMemo(
    () => makeSelectTipSubtitle(orderType),
    [orderType],
  );
  const checkoutTipSubtitle = useSelector(selectCheckoutTipSubtitle);
  const { open, handleClose, handleOpen } = useCustomDialog();

  return (
    <CheckoutSection
      title={t('checkout.orderSummary.title')}
      data-testid="CheckoutSummarySection.CheckoutSection"
      {...rest}
    >
      <Box mb={1}>
        {subtotal && (
          <TotalItem data-testid="CheckoutSummarySection.Subtotal">
            <Typography>{t('checkout.orderSummary.subtotal')}</Typography>
            <Typography>${subtotal}</Typography>
          </TotalItem>
        )}

        <Box>
          {charges &&
            charges.map(
              ({ chargeDescription, displayChargeApplied }, index) => (
                <TotalItem
                  key={index}
                  data-testid="CheckoutSummarySection.Charge"
                >
                  <Typography>{chargeDescription}</Typography>
                  <Typography>${displayChargeApplied}</Typography>
                </TotalItem>
              ),
            )}

          {taxes &&
            taxes.map(({ taxDescription, displayTaxAmountApplied }, index) => (
              <TotalItem key={index} data-testid="CheckoutSummarySection.Tax">
                <Typography>{taxDescription}</Typography>
                <Typography>${displayTaxAmountApplied}</Typography>
              </TotalItem>
            ))}

          {displayTotalWithTip > 0 && (
            <TotalItem>
              <Typography>{t('checkout.orderSummary.discounts')}</Typography>
              <Typography>
                <ActionLink
                  onClick={handleOpen}
                  data-testid="CheckoutSummarySection.ActionLink.PromoCode"
                >
                  {t('checkout.orderSummary.promoCode')}
                </ActionLink>
              </Typography>
            </TotalItem>
          )}

          {discounts?.map(discount => (
            <AppliedOffer discount={discount} key={discount.discountId} />
          ))}

          {showTip && (
            <>
              <Box my={1}>
                <Divider />
              </Box>
              <Box mb={1}>
                <TotalItem data-testid="CheckoutSummarySection.Tip">
                  <Typography
                    component="h3"
                    variant="body1"
                    id="tip-label"
                    data-testid="CheckoutSummarySection.Tip.Title"
                  >
                    {checkoutTipHeader || t('checkout.orderSummary.tip')}
                  </Typography>

                  <Typography>${getRoundedAmount(tip)}</Typography>
                </TotalItem>
                {checkoutTipSubtitleEnabled ? (
                  <Typography
                    component="p"
                    variant="body2"
                    data-testid="CheckoutSummarySection.Tip.Subtitle"
                  >
                    {checkoutTipSubtitle}
                  </Typography>
                ) : null}
              </Box>
              <CheckoutTip />
            </>
          )}
        </Box>
      </Box>

      <Divider />

      <Box
        mt={1}
        display="flex"
        justifyContent="space-between"
        data-testid="CheckoutSummarySection.Total"
      >
        <Typography component="h3" variant="h6">
          <span>{t('checkout.orderSummary.total')}</span>
        </Typography>
        <Typography component="p" variant="h6">
          ${displayTotalWithTip}
        </Typography>
      </Box>

      <CheckoutPromoCode open={open} handleClose={handleClose} />
    </CheckoutSection>
  );
};

export default CheckoutSummarySection;
